import { FC, KeyboardEvent, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import cx from 'classnames';
import Modal from '../../../components/shared/Modal/Modal';
import Field from '../../../components/shared/Field/Field';
import Selector, { SelectorType } from '../../../components/shared/Selector/Selector';
import RadioButton from '../../../components/shared/RadioButton/RadioButton';
import BtButton from '../../bt-button/bt-button';
import styles from './priceFieldRowModal.module.scss';
import {getPriceUnits} from '../../../services/priceUnitService';
import { TEXT_UNIT_VALUE, buildNewPriceFieldRow } from './newPriceFieldRow';
import {
  BUDGET_VARIABLE,
  formulaVariables,
  insertVariable,
  isValidFormula,
  removeVariableBefore,
  toDisplayFormula,
  toStoredFormula,
  withUniqueNames,
} from '../../../util/formula';
import { NewPriceFieldRow, OrderedRow } from '../../../models/priceRows.model';

type RowContent = 'value' | 'formula';

type Props = {
  row?: OrderedRow;
  otherRows: OrderedRow[];
  onClose: () => void;
  onSave: (row: NewPriceFieldRow) => void;
  onDelete: () => void;
};

const sameName = (a: string, b: string) => a.trim().toLocaleLowerCase('sv') === b.trim().toLocaleLowerCase('sv');

export const PriceFieldRowModal: FC<Props> = ({ row, otherRows, onClose, onSave, onDelete }) => {
  const [t] = useTranslation('projects', { keyPrefix: 'singlePurchase.bidOverview' });
  const [tValidation] = useTranslation('validation');
  const textUnitOption = { label: t('modal.formula.new.textUnit', 'Text'), value: TEXT_UNIT_VALUE };
  const budgetVariable = { id: BUDGET_VARIABLE, name: t('modal.formula.new.budget', 'Budget') };
  const textRowIds = new Set(otherRows.filter(other => other.kind === 'text').map(other => other.id));
  const formulaChips = withUniqueNames([...otherRows, budgetVariable]);
  const variables = formulaChips.filter(chip => !textRowIds.has(chip.id));
  const [name, setName] = useState(row?.name ?? '');
  const [unit, setUnit] = useState<SelectorType>(row?.kind === 'text' ? textUnitOption : null);
  const [units, setUnits] = useState<SelectorType[]>([]);
  const [content, setContent] = useState<RowContent>(row?.kind === 'formula' ? 'formula' : 'value');
  const [formula, setFormula] = useState(() => toDisplayFormula(row?.kind === 'formula' ? row.formula : '', variables));
  const formulaInputRef = useRef<HTMLInputElement>(null);
  const caretAfterEdit = useRef<number | null>(null);
  const [hasTriedToSave, setHasTriedToSave] = useState(false);

  useEffect(() => {
    (async () => {
      const allUnits = await getPriceUnits();
      const units = allUnits
        .map((d: { _id: string, name: string }) => ({ label: d.name, value: d._id }));
      setUnits(units);
    })();
  }, []);

  useLayoutEffect(() => {
    const input = formulaInputRef.current;
    if (!input || caretAfterEdit.current === null) return;
    input.focus();
    input.setSelectionRange(caretAfterEdit.current, caretAfterEdit.current);
    caretAfterEdit.current = null;
  }, [formula]);

  const unitOptions = [...units, textUnitOption];
  const savedUnit = row?.kind === 'value' || row?.kind === 'formula' ? row.unit : undefined;
  const selectedUnit = unit ?? units.find(option => option?.label === savedUnit) ?? units[0] ?? null;
  const isTextRow = selectedUnit?.value === TEXT_UNIT_VALUE;
  const selectedContent: RowContent = isTextRow ? 'value' : content;
  const isFormulaRow = selectedContent === 'formula';
  const isFormulaInvalid = isFormulaRow && !isValidFormula(formula, variables.map(variable => variable.name));
  const isNameTaken = [...otherRows.map(other => other.name), budgetVariable.name].some(taken => sameName(taken, name));
  const isNameMissing = !name.trim();
  const rowsUsingThisRow = otherRows.filter(
    other => other.kind === 'formula' && !!row && formulaVariables(other.formula).includes(row.id),
  );
  const isDeleteBlocked = rowsUsingThisRow.length > 0;

  const nameError = () => {
    if (isNameTaken) return t('modal.formula.new.nameTaken', 'Det finns redan en rad med det namnet');
    if (isNameMissing && hasTriedToSave) return tValidation('requiredField', 'Obligatoriskt fält');
    return undefined;
  };

  const insertIntoFormula = (variableName: string) => {
    const input = formulaInputRef.current;
    const inserted = insertVariable(
      formula,
      variableName,
      input?.selectionStart ?? formula.length,
      input?.selectionEnd ?? formula.length,
    );
    caretAfterEdit.current = inserted.caret;
    setFormula(inserted.formula);
  };

  const removeWholeVariableOnBackspace = (e: KeyboardEvent<HTMLInputElement>) => {
    const { selectionStart, selectionEnd } = e.currentTarget;
    if (e.key !== 'Backspace' || selectionStart === null || selectionStart !== selectionEnd) return;
    const removed = removeVariableBefore(formula, selectionStart);
    if (!removed) return;
    e.preventDefault();
    caretAfterEdit.current = removed.caret;
    setFormula(removed.formula);
  };

  const save = () => {
    setHasTriedToSave(true);
    if (isNameMissing || isNameTaken || isFormulaInvalid) return;
    onSave(buildNewPriceFieldRow(name, selectedUnit, isFormulaRow ? toStoredFormula(formula, variables) : undefined));
  };

  const confirmDelete = () => {
    const isConfirmed = window.confirm(
      t(
        'modal.formula.edit.deleteConfirm',
        'Vill du ta bort raden {{name}}? Det som har fyllts i på raden tas också bort.',
        { name: row?.name },
      ),
    );
    if (isConfirmed) onDelete();
  };

  const deleteButton = row && (
    <div className={styles.delete}>
      <BtButton
        color="white"
        disabled={isDeleteBlocked}
        onClick={isDeleteBlocked ? undefined : confirmDelete}
      >
        {t('buttons.deleteRow', 'Ta bort')}
      </BtButton>
      {isDeleteBlocked && (
        <span className={styles.deleteBlocked}>
          {t('modal.formula.edit.usedBy', 'Används i formeln för {{names}}', {
            names: rowsUsingThisRow.map(other => other.name).join(', '),
          })}
        </span>
      )}
    </div>
  );

  return (
    <Modal
      title={row ? t('modal.formula.edit.title', 'Edit Row') : t('modal.formula.new.title', 'New Row')}
      show
      setShow={onClose}
      buttonInfo={{
        label: t('buttons.save', 'Save'),
        action: save,
        loaderShow: false,
      }}
      containerBeforeButtons={deleteButton}
    >
      <div className={styles.column}>
        <div className={styles.row}>
          <Field
            className={styles.name}
            placeholder={t('modal.formula.new.namePlaceholder', 'Namn')}
            value={name}
            onChange={setName}
            errorMessage={nameError()}
          />
          <Selector className={styles.unit} options={unitOptions} value={selectedUnit} onChange={setUnit} />
        </div>
        <div className={styles.content}>
          <span className={styles.label}>{t('modal.formula.new.content', 'Innehåll')}</span>
          <div className={styles.contentOptions}>
            <div className={cx(styles.contentOption, { [styles.selected]: selectedContent === 'value' })}>
              <RadioButton
                checked={selectedContent === 'value'}
                onChange={() => setContent('value')}
                label={
                  <span className={styles.optionLabel}>
                    <span className={styles.optionTitle}>{t('modal.formula.new.value', 'Värde')}</span>
                    <span className={styles.optionDescription}>
                      {t('modal.formula.new.valueDescription', 'Du skriver ett tal eller en text i varje cell på raden')}
                    </span>
                  </span>
                }
              />
            </div>
            <div
              className={cx(styles.contentOption, {
                [styles.selected]: selectedContent === 'formula',
                [styles.disabled]: isTextRow,
              })}
            >
              <RadioButton
                checked={selectedContent === 'formula'}
                onChange={() => setContent('formula')}
                label={
                  <span className={styles.optionLabel}>
                    <span className={styles.optionTitle}>{t('modal.formula.new.formula', 'Formel')}</span>
                    <span className={styles.optionDescription}>
                      {t(
                        'modal.formula.new.formulaDescription',
                        'Raden räknas ut från andra rader. Klicka på en rad nedan för att lägga till den, och använd + - * / och parenteser.',
                      )}
                    </span>
                  </span>
                }
              />
              <div className={styles.priceFieldChips}>
                {formulaChips.map(chip => (
                  <BtButton
                    key={chip.id}
                    color="white"
                    size="xxs"
                    disabled={textRowIds.has(chip.id)}
                    onClick={() => insertIntoFormula(chip.name)}
                  >
                    {chip.name}
                  </BtButton>
                ))}
              </div>
              <Field
                ref={formulaInputRef}
                placeholder={t('modal.formula.new.formulaPlaceholder', '[Budget] - [Totalpris]')}
                value={formula}
                onChange={setFormula}
                onKeyDown={removeWholeVariableOnBackspace}
                onFocus={() => setContent('formula')}
                errorMessage={
                  isFormulaInvalid && (formula.trim() || hasTriedToSave)
                    ? t('modal.formula.new.invalidFormula', 'Formeln är inte giltig')
                    : undefined
                }
              />
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};
